<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { messageStamp } from 'shared/helpers/timeHelper';

import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'next/pagination/PaginationFooter.vue';
import Button from 'next/button/Button.vue';

const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { t } = useI18n();

const currentPage = ref(1);
let searchTimeout = null;

const executions = computed(
  () => store.getters['wgptTaskExecutions/getExecutions']
);
const meta = computed(() => store.getters['wgptTaskExecutions/getMeta']);
const uiFlags = computed(() => store.getters['wgptTaskExecutions/getUIFlags']);

const isFetching = computed(() => uiFlags.value.isFetching);
const totalItems = computed(() => meta.value.total || 0);
const itemsPerPage = computed(() => meta.value.size || 15);

const fetchExecutions = page => {
  currentPage.value = page;
  store.dispatch('wgptTaskExecutions/get', {
    page,
    q: props.searchQuery.trim(),
  });
};

const refresh = () => {
  fetchExecutions(currentPage.value);
};

watch(
  () => props.searchQuery,
  () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      currentPage.value = 1;
      fetchExecutions(1);
    }, 300);
  }
);

const toTimestamp = date => (date ? new Date(date).getTime() / 1000 : null);
const formatDatePart = date => {
  const ts = toTimestamp(date);
  return ts ? messageStamp(ts, 'LLL d, yyyy') : '-';
};
const formatTimePart = date => {
  const ts = toTimestamp(date);
  return ts ? messageStamp(ts, 'hh:mm a') : '';
};

const statusClass = status => {
  if (status === 'success') return 'bg-n-teal-3 text-n-teal-11';
  if (status === 'failed') return 'bg-n-ruby-3 text-n-ruby-11';
  if (status === 'scheduled') return 'bg-n-blue-3 text-n-blue-11';
  if (status === 'cancelled') return 'bg-n-amber-3 text-n-amber-11';
  if (status === 'skipped') return 'bg-n-slate-3 text-n-slate-11';
  return 'bg-n-slate-3 text-n-slate-11';
};

const statusLabel = status =>
  t(`WGPT_TASKS.EXECUTIONS.STATUS.${status.toUpperCase()}`);

const hasContent = value =>
  Array.isArray(value)
    ? value.length > 0
    : value && Object.keys(value).length > 0;

const expandedId = ref(null);
const expandedSection = ref(null);
const toggleExpand = (id, section) => {
  if (expandedId.value === id && expandedSection.value === section) {
    expandedId.value = null;
    expandedSection.value = null;
  } else {
    expandedId.value = id;
    expandedSection.value = section;
  }
};

const cancelExecution = id => {
  store.dispatch('wgptTaskExecutions/cancel', id);
};

const runNowExecution = id => {
  store.dispatch('wgptTaskExecutions/runNow', id);
};

onMounted(() => {
  store.dispatch('wgptTaskExecutions/clear');
  fetchExecutions(1);
});

defineExpose({ refresh });
</script>

<template>
  <div class="h-full flex flex-col">
    <div class="flex-1 overflow-auto">
      <div
        v-if="isFetching && !executions.length"
        class="flex items-center justify-center py-16"
      >
        <SpinnerLoader :size="24" class="text-n-blue-9" />
      </div>

      <div
        v-else-if="executions.length"
        class="rounded-lg border border-n-weak"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak text-n-slate-11">
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.UUID') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.TASK_NAME') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.STATUS') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.SCHEDULED_AT') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.SCHEDULED_FOR') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.PARAMETERS') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.RESULTS') }}
              </th>
              <th class="px-3 py-2.5 text-start font-medium">
                {{ t('WGPT_TASKS.EXECUTIONS.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="execution in executions" :key="execution.id">
              <tr class="border-b border-n-weak last:border-b-0">
                <td class="px-3 py-2.5 text-n-slate-11 font-mono text-xs">
                  {{ execution.uuid }}
                </td>
                <td class="px-3 py-2.5 text-n-slate-12 font-medium">
                  {{ execution.task_name }}
                </td>
                <td class="px-3 py-2.5">
                  <span
                    class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                    :class="statusClass(execution.status)"
                  >
                    {{ statusLabel(execution.status) }}
                  </span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">
                  <div class="flex flex-col">
                    <span>{{ formatDatePart(execution.scheduled_at) }}</span>
                    <span class="text-xs text-n-slate-9">{{
                      formatTimePart(execution.scheduled_at)
                    }}</span>
                  </div>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11 whitespace-nowrap">
                  <div class="flex flex-col">
                    <span>{{ formatDatePart(execution.scheduled_for) }}</span>
                    <span class="text-xs text-n-slate-9">{{
                      formatTimePart(execution.scheduled_for)
                    }}</span>
                  </div>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11">
                  <button
                    v-if="hasContent(execution.parameters)"
                    class="text-n-blue-11 hover:underline text-xs"
                    @click="toggleExpand(execution.id, 'parameters')"
                  >
                    {{
                      expandedId === execution.id &&
                      expandedSection === 'parameters'
                        ? t('WGPT_TASKS.EXECUTIONS.TABLE.HIDE')
                        : t('WGPT_TASKS.EXECUTIONS.TABLE.VIEW')
                    }}
                  </button>
                  <span v-else class="text-n-slate-8">&mdash;</span>
                </td>
                <td class="px-3 py-2.5 text-n-slate-11">
                  <button
                    v-if="hasContent(execution.outcome)"
                    class="text-n-blue-11 hover:underline text-xs"
                    @click="toggleExpand(execution.id, 'outcome')"
                  >
                    {{
                      expandedId === execution.id &&
                      expandedSection === 'outcome'
                        ? t('WGPT_TASKS.EXECUTIONS.TABLE.HIDE')
                        : t('WGPT_TASKS.EXECUTIONS.TABLE.VIEW')
                    }}
                  </button>
                  <span v-else class="text-n-slate-8">&mdash;</span>
                </td>
                <td class="px-3 py-2.5">
                  <div class="flex gap-2 justify-end">
                    <Button
                      v-tooltip.top="t('WGPT_TASKS.EXECUTIONS.TABLE.RUN_NOW')"
                      icon="i-lucide-play"
                      slate
                      sm
                      :disabled="execution.status !== 'scheduled'"
                      class="hover:enabled:text-n-green-11 hover:enabled:bg-n-green-2"
                      @click="runNowExecution(execution.id)"
                    />
                    <Button
                      v-tooltip.top="t('WGPT_TASKS.EXECUTIONS.TABLE.CANCEL')"
                      icon="i-lucide-x-circle"
                      slate
                      sm
                      :disabled="execution.status !== 'scheduled'"
                      class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                      @click="cancelExecution(execution.id)"
                    />
                  </div>
                </td>
              </tr>
              <tr
                v-if="expandedId === execution.id"
                class="border-b border-n-weak last:border-b-0"
              >
                <td colspan="8" class="px-3 py-3">
                  <pre
                    class="text-xs bg-n-slate-2 rounded-lg p-3 overflow-y-auto max-h-60 whitespace-pre-wrap break-all"
                    >{{
                      JSON.stringify(
                        expandedSection === 'parameters'
                          ? execution.parameters
                          : execution.outcome,
                        null,
                        2
                      )
                    }}</pre
                  >
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div
        v-else
        class="py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ t('WGPT_TASKS.EXECUTIONS.EMPTY') }}
      </div>
    </div>
    <PaginationFooter
      v-if="totalItems > 0"
      :current-page="currentPage"
      :total-items="totalItems"
      :items-per-page="itemsPerPage"
      @update:current-page="fetchExecutions"
    />
  </div>
</template>
